import type { Command } from 'commander';
import { unwrapDataArray } from '@wport/core';
import { personalGet, type PersonalRequestOptions } from '../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../lib/global-opts';
import { printJson, printTable } from '../../lib/output';
import { OAUTH_SESSIONS_BASE, SessionItem } from '@wport/core';

/** 格式化沿 enterprise/keys/list.ts 的 formatDate 模式（每個 list 命令各自複製一份，檔案小而聚焦）。 */
function formatDate(value: string | null | undefined): string {
	return value ? String(value).slice(0, 10) : '';
}

/**
 * `sessions list` 核心：GET /oauth/sessions → 列出所有存活的登入 session（跨裝置）。
 * `is_current` 標記本機這台裝置，方便使用者辨識忘記登出的其他裝置（DR-7 失竊自救場景的前置偵查）。
 */
export async function runSessionsList(ctx: ResolvedContext): Promise<void> {
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const { body } = await personalGet(opts, OAUTH_SESSIONS_BASE);
	const sessions = unwrapDataArray<SessionItem>(body);

	if (ctx.format === 'json') {
		printJson(sessions);
		return;
	}

	printTable(
		sessions,
		[
			{ header: 'ENC_ID', value: (r) => r.enc_id.slice(0, 14) },
			{ header: 'DEVICE', value: (r) => r.device_name ?? '', maxWidth: 24 },
			{ header: 'CREATED', value: (r) => formatDate(r.created_at), maxWidth: 12 },
			{ header: 'LAST_USED', value: (r) => formatDate(r.last_used_at), maxWidth: 12 },
			{ header: 'CURRENT', value: (r) => (r.is_current ? '*' : '') },
		],
		ctx.color
	);
}

export function registerSessionsList(parent: Command): void {
	parent
		.command('list')
		.description('List your active personal login sessions (devices)')
		.action(async (_flags: unknown, command: Command) => {
			await runSessionsList(resolveContext(command));
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { formatDate };
